export const COUNTRY_UNITED_STATES = 'United States' as const;
export const COUNTRY_CANADA = 'Canada' as const;

export type ShipToCountry = typeof COUNTRY_UNITED_STATES | typeof COUNTRY_CANADA;

export interface RegionOption {
    value: string;
    label: string;
}

export const COUNTRY_OPTIONS: RegionOption[] = [
    { value: COUNTRY_UNITED_STATES, label: 'United States' },
    { value: COUNTRY_CANADA, label: 'Canada' }
];

// Mirrors backend/src/utils/countryIso.ts exactly - FE country classification
// must never disagree with the BE's vendor routing (biolux vs orthoarch).
const US_COUNTRY_ALIASES = new Set([
    'us',
    'usa',
    'united states',
    'united states of america',
    'u.s.',
    'u.s.a.'
]);
const CA_COUNTRY_ALIASES = new Set(['ca', 'can', 'canada']);

// Unrecognized or empty input defaults to United States, per OrthoPulse's
// current US/Canada-only shipping support.
export function normalizeCountry(raw: string | null | undefined): ShipToCountry {
    const key = (raw ?? '').trim().toLowerCase();
    if (CA_COUNTRY_ALIASES.has(key)) return COUNTRY_CANADA;
    if (US_COUNTRY_ALIASES.has(key)) return COUNTRY_UNITED_STATES;
    return COUNTRY_UNITED_STATES;
}

export const US_STATES: RegionOption[] = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'DC', label: 'District of Columbia' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
];

export const CA_PROVINCES: RegionOption[] = [
    { value: 'AB', label: 'Alberta' },
    { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' },
    { value: 'NS', label: 'Nova Scotia' },
    { value: 'NT', label: 'Northwest Territories' },
    { value: 'NU', label: 'Nunavut' },
    { value: 'ON', label: 'Ontario' },
    { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' },
    { value: 'SK', label: 'Saskatchewan' },
    { value: 'YT', label: 'Yukon' }
];

export function regionOptionsForCountry(country: ShipToCountry): RegionOption[] {
    return country === COUNTRY_CANADA ? CA_PROVINCES : US_STATES;
}

export function regionFieldLabel(country: ShipToCountry): string {
    return country === COUNTRY_CANADA ? 'Province' : 'State';
}

export function postalFieldLabel(country: ShipToCountry): string {
    return country === COUNTRY_CANADA ? 'Postal code' : 'ZIP code';
}

// A handful of common non-standard abbreviations seen in free-text address
// data (e.g. an operator typing "Ont." instead of the ISO code).
const REGION_ABBREVIATION_ALIASES: Record<string, string> = {
    calif: 'CA',
    fla: 'FL',
    mass: 'MA',
    penn: 'PA',
    tex: 'TX',
    wash: 'WA',
    ont: 'ON',
    que: 'QC',
    alta: 'AB',
    sask: 'SK',
    man: 'MB',
    nfld: 'NL',
    pei: 'PE',
    nwt: 'NT'
};

function stripTrailingPeriods(value: string): string {
    return value.replace(/\.+$/, '');
}

// Accepts a two-letter code, the full region name, or a common abbreviation
// (e.g. "Ont." / "ont"), case-insensitive and tolerant of trailing periods.
// Unmatched input (including empty) resolves to '' - the caller is
// responsible for surfacing a dropped-value notice when raw was non-empty.
export function normalizeRegion(raw: string | null | undefined, country: ShipToCountry): string {
    const key = stripTrailingPeriods((raw ?? '').trim().toLowerCase());
    if (!key) return '';

    const options = regionOptionsForCountry(country);
    const direct = options.find(
        (option) => option.value.toLowerCase() === key || option.label.toLowerCase() === key
    );
    if (direct) return direct.value;

    const abbreviated = REGION_ABBREVIATION_ALIASES[key];
    if (abbreviated && options.some((option) => option.value === abbreviated)) {
        return abbreviated;
    }

    return '';
}
